import type { FunctionComponent } from "react"

import { useParams, usePathname } from "next/navigation"

import { BreadcrumbTrail } from "@jobteaser/ui-kit/components/BreadcrumbTrail/BreadcrumbTrail"

import { useCompanyArticleDetailsFetch } from "@/modules/companies/hooks/useCompanyArticleDetailsFetch"
import { useCompanyFetch } from "@/modules/companies/hooks/useCompanyFetch"
import {
  COMPANY_BASENAME,
  COMPANY_JOB_OFFERS,
  COMPANY_NEWSFEED,
  COMPANY_TEAMS,
  NEWSFEED_PAGE_ROUTE,
  TEAMS_PAGE_ROUTE,
} from "@/modules/companies/utils/routing"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useJobAdsFromAlgolia } from "@/modules/jobAds/client/hooks/useJobAdsFromAlgolia"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"

import styles from "./Breadcrumbs.module.css"

export const Breadcrumbs: FunctionComponent = () => {
  const { t } = useTranslation(["companies_client"])
  const locale = useLocale()
  const { companySlug } = useParams<{ companySlug: string }>()

  const pathname = usePathname()
  const tabPath = pathname.split(companySlug)[1]

  const { dataCompany, isSuccessCompany } = useCompanyFetch()
  const { dataJobAds, isSuccessJobAds } = useJobAdsFromAlgolia()
  const { dataCompanyArticleDetails, isSuccessCompanyArticleDetails } = useCompanyArticleDetailsFetch()

  const dataJobAdsCount = dataJobAds?.totalSize || 0

  if (!isSuccessCompany || !isSuccessJobAds || !dataCompany) {
    return null
  }

  const routes = [COMPANY_TEAMS, COMPANY_JOB_OFFERS, COMPANY_NEWSFEED]
  const currentTab = routes.filter(path => tabPath.includes(path))[0]

  // fixme, unknown type should be changed to the (future) type defined inside ui-kit
  const breadcrumbs: unknown[] = [
    { href: `/${locale}`, title: t("companies_client.breadcrumbs.home") },
    { href: `/${locale}/${COMPANY_BASENAME}`, title: t("companies_client.breadcrumbs.companies") },
    {
      component: Link,
      href: `/${locale}/${COMPANY_BASENAME}/${companySlug}`,
      title: dataCompany.page.name,
    },
  ]

  if (currentTab === COMPANY_TEAMS) {
    breadcrumbs.push({ title: t(TEAMS_PAGE_ROUTE.translationKey) })
  }

  if (currentTab === COMPANY_JOB_OFFERS) {
    const jobOffersItem = {
      title: t("companies_client.meta.title.suffix.jobOffers", {
        count: dataJobAdsCount,
      }),
    }

    breadcrumbs.push(jobOffersItem)
  }

  if (currentTab === COMPANY_NEWSFEED) {
    if (pathname.endsWith(COMPANY_NEWSFEED)) {
      breadcrumbs.push({ title: t(NEWSFEED_PAGE_ROUTE.translationKey) })
    } else if (isSuccessCompanyArticleDetails) {
      breadcrumbs.push(
        {
          component: Link,
          href: `/${locale}/${COMPANY_BASENAME}/${companySlug}/${COMPANY_NEWSFEED}`,
          title: t(NEWSFEED_PAGE_ROUTE.translationKey),
        },
        {
          title: dataCompanyArticleDetails?.article.title,
        }
      )
    }
  }

  return (
    <BreadcrumbTrail breadcrumbs={breadcrumbs} className={styles.container} data-testid="company_breadcrumb_block" />
  )
}
